import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import defaultFoodImage from './assets/DefaultFood.jpg';
import {
    fetchFoodItemById,
    formatFoodItemDisplayName,
    formatOnlineIngredientsDisplay,
    resolveFoodItemImageFields,
} from './onlineFoodItems';
import { findMenuFoodItemById } from './foodItemRecipeUtils';
import { formatNutritionLabel, resolveNutritionMap } from './foodNutrition';
import { formatTemplateSourceLabel, normalizeTemplateSource } from './dailyMenuApi';
import { recipeTabPath } from './FoodItemRecipePage';
import { ROUTES } from '../constants/appRoutes';
import DietTypeSymbol from './DietTypeSymbol';
import './css/Home.css';
import './css/Menu.css';
import './css/Restaurants.css';
import './css/OrderDetails.css';

const RESTAURANTS_HOME_TAB = 'restaurants';
export const FOOD_ITEM_DETAIL_TAB_PREFIX = 'restaurants/menu-item';

export function foodItemDetailTabPath(foodItemId, menuType, channel) {
    const params = new URLSearchParams();
    params.set('tab', `${FOOD_ITEM_DETAIL_TAB_PREFIX}/${foodItemId}`);
    if (menuType) params.set('menuType', menuType);
    if (channel) params.set('channel', channel);
    return `${ROUTES.businessAdmin}?${params.toString()}`;
}

function getCurrencySymbol(currency) {
    return String(currency ?? 'INR').toUpperCase() === 'USD' ? '$' : '₹';
}

function formatPriceWithCurrency(price, currency) {
    if (price == null || price === '') return '—';
    return `${getCurrencySymbol(currency)}${price}`;
}

function displayValue(value) {
    const text = value != null ? String(value).trim() : '';
    return text || '—';
}

function ingredientsListFromItem(item) {
    if (!item) return [];
    if (Array.isArray(item.ingredients) && item.ingredients.length > 0) {
        return item.ingredients
            .map((ing) => ing.ingredientName || ing.ingredient_name || ing.name || '')
            .filter(Boolean);
    }
    const display = formatOnlineIngredientsDisplay(item);
    if (!display) return [];
    return display
        .split(/[,;]/)
        .map((part) => part.trim())
        .filter(Boolean);
}

function normalizeAgePricingRows(item) {
    const raw = item?.agePricing ?? item?.age_pricing;
    if (!Array.isArray(raw)) return [];
    return raw.map((tier) => ({
        agePricingId: tier.agePricingId ?? tier.Age_Pricing_Id ?? null,
        minAge: tier.minAge ?? tier.Min_Age ?? '',
        maxAge: tier.maxAge ?? tier.Max_Age ?? '',
        quantity: tier.quantity ?? tier.Quantity ?? '',
        price: tier.price ?? tier.Price ?? '',
        status: tier.status ?? tier.Status ?? 'ACTIVE',
    }));
}

function DetailFact({ label, value, highlight = false, alwaysShow = false }) {
    const text = value != null ? String(value).trim() : '';
    if (!alwaysShow && !text) return null;
    return (
        <div className="food-item-detail-fact">
            <dt className="food-item-detail-fact-label">{label}</dt>
            <dd
                className={`food-item-detail-fact-value${
                    highlight ? ' food-item-detail-fact-value--accent' : ''
                }`}
            >
                {text || '—'}
            </dd>
        </div>
    );
}

async function fetchCateringMenuItem(restaurantId, itemId) {
    const token = localStorage.getItem('Token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await axiosRestaurantInstance.get(`/catering/menu/${restaurantId}`, { headers });
    const data = response.data || {};
    const items =
        data.items && Array.isArray(data.items)
            ? data.items
            : data.cateringItemId != null
              ? [data]
              : [];
    const match = items.find(
        (row) => Number(row.cateringItemId ?? row.Catering_Item_Id) === Number(itemId)
    );
    if (!match) return null;
    const cat =
        match.foodCategory ??
        match.foodCategoryName ??
        match.Food_Category ??
        'Other';
    const { imagePath, imageUrl } = resolveFoodItemImageFields(match);
    return {
        Food_Item_Id: match.cateringItemId ?? match.Catering_Item_Id,
        cateringItemId: match.cateringItemId ?? match.Catering_Item_Id,
        foodCategory: cat,
        Food_Category: cat,
        itemFoodType: match.foodType ?? match.itemFoodType ?? 'VEG',
        Food_Item: match.itemName ?? match.Item_Name ?? match.foodItem ?? '',
        Price: match.price ?? match.Price,
        Quantity: match.quantity ?? match.servingSize ?? match.Quantity ?? '',
        Description: match.description ?? match.Description ?? '',
        isAvailable: match.isAvailable ?? match.Is_available ?? true,
        imagePath,
        imageUrl: imageUrl || undefined,
    };
}

function FoodItemDetailRouteRedirect({ foodItemId }) {
    const [searchParams] = useSearchParams();
    const menuType = searchParams.get('menuType');
    return <Navigate to={foodItemDetailTabPath(foodItemId, menuType)} replace />;
}

function FoodItemDetailSkeleton() {
    return (
        <div className="food-item-detail-skeleton" aria-hidden="true">
            <div className="food-item-detail-skeleton-hero">
                <div className="food-item-detail-skeleton-image" />
                <div className="food-item-detail-skeleton-lines">
                    <div className="food-item-detail-skeleton-line food-item-detail-skeleton-line--short" />
                    <div className="food-item-detail-skeleton-line food-item-detail-skeleton-line--title" />
                    <div className="food-item-detail-skeleton-line" />
                    <div className="food-item-detail-skeleton-line food-item-detail-skeleton-line--price" />
                </div>
            </div>
            <div className="food-item-detail-skeleton-card" />
            <div className="food-item-detail-skeleton-card" />
        </div>
    );
}

const FoodItemDetailPageView = ({
    foodItemId,
    menuTypeFilter = 'online',
    menuSource = 'MYEATS',
    restaurantId,
    menuItems = {},
    onBack,
    onRecipeNavigate,
    embedded = false,
}) => {
    const navigate = useNavigate();
    const [item, setItem] = useState(null);
    const [agePricing, setAgePricing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const itemId = Number(foodItemId);
    const isCatering = menuTypeFilter === 'catering';
    const menuSourceNorm = normalizeTemplateSource(
        item?.Menu_Source ?? item?.menuSource ?? menuSource
    );
    const isJreats = !isCatering && menuSourceNorm === 'JREATS';

    const loadItem = useCallback(async () => {
        if (!itemId || Number.isNaN(itemId)) {
            setError('Invalid menu item id.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError('');
        try {
            let row = null;
            if (isCatering) {
                const rid = Number(restaurantId || localStorage.getItem('restaurantId') || 0);
                if (!rid) {
                    setError('No restaurant selected.');
                    setItem(null);
                    setAgePricing([]);
                    return;
                }
                row =
                    findMenuFoodItemById(menuItems, itemId) ||
                    (await fetchCateringMenuItem(rid, itemId));
            } else {
                row = await fetchFoodItemById(itemId);
            }

            if (!row) {
                setError('Menu item not found.');
                setItem(null);
                setAgePricing([]);
                return;
            }

            setItem(row);
            setAgePricing(normalizeAgePricingRows(row));
        } catch (err) {
            console.error('Food item detail load failed:', err?.response?.data || err?.message);
            setError(
                err?.response?.data?.message ||
                    err?.response?.data?.error ||
                    err?.message ||
                    'Failed to load menu item.'
            );
            setItem(null);
            setAgePricing([]);
        } finally {
            setLoading(false);
        }
    }, [itemId, menuItems, menuTypeFilter, restaurantId, isCatering]);

    useEffect(() => {
        loadItem();
    }, [loadItem]);

    const imageUrl = useMemo(() => {
        if (!item) return '';
        const direct = item.imageUrl ?? item.image_url ?? item.ImageUrl;
        if (direct) return String(direct).replace(/%22|"$/g, '').trim();
        const path = item.imagePath ?? item.image_path ?? item.Image_Path;
        if (!path) return '';
        const base =
            axiosRestaurantInstance.defaults.baseURL ||
            process.env.REACT_APP_API_COMPANY_URL ||
            '';
        if (!base) return '';
        try {
            return `${new URL(base).origin}/uploads/${String(path).replace(/^\//, '')}`;
        } catch {
            return `${base.replace(/\/$/, '')}/uploads/${String(path).replace(/^\//, '')}`;
        }
    }, [item]);

    const nutritionEntries = useMemo(() => {
        const map = resolveNutritionMap(item ?? {});
        return Object.entries(map).filter(([, val]) => displayValue(val) !== '—');
    }, [item]);

    const ingredientNames = useMemo(() => ingredientsListFromItem(item), [item]);

    const handleBack = () => {
        if (typeof onBack === 'function') {
            onBack();
            return;
        }
        navigate(`${ROUTES.businessAdmin}?tab=${RESTAURANTS_HOME_TAB}`);
    };

    const handleRecipe = () => {
        if (typeof onRecipeNavigate === 'function') {
            onRecipeNavigate(itemId);
            return;
        }
        navigate(recipeTabPath(itemId, 'view', menuTypeFilter));
    };

    const itemName = item ? formatFoodItemDisplayName(item.Food_Item) : '';
    const categoryLabel = item?.foodCategory ?? item?.Food_Category ?? item?.Food_Type ?? '';
    const cuisineOrigin = item?.cuisineOrigin ?? item?.Cuisine_Origin ?? '';
    const categoryLine = categoryLabel != null ? String(categoryLabel).trim() : '';
    const cuisineLine = cuisineOrigin != null ? String(cuisineOrigin).trim() : '';
    const priceLine = formatPriceWithCurrency(
        item?.Price ?? item?.price,
        item?.Currency ?? item?.currency
    );
    const servingLine = displayValue(item?.Quantity ?? item?.quantity);
    const description = item?.Description ?? item?.description ?? '';
    const notes = item?.Notes ?? item?.notes ?? '';

    const detailBody = (
        <div className="menu food-item-detail-page">
            <div className="food-item-detail-shell">
                <header className="food-item-detail-topbar">
                    <button
                        type="button"
                        className="food-item-detail-back"
                        onClick={handleBack}
                        aria-label="Back to menu"
                    >
                        <FaArrowLeft aria-hidden />
                        <span>Back to menu</span>
                    </button>
                    {!loading && !error && item ? (
                        <div className="food-item-detail-topbar-end">
                            <span className="food-item-detail-topbar-id">
                                ID {item.Food_Item_Id ?? item.cateringItemId}
                            </span>
                            <button
                                type="button"
                                className="food-item-detail-recipe-btn"
                                onClick={handleRecipe}
                            >
                                View recipe
                            </button>
                        </div>
                    ) : null}
                </header>

                {loading ? <FoodItemDetailSkeleton /> : null}

                {!loading && error ? (
                    <div className="food-item-detail-empty">
                        <p className="food-item-detail-empty-title">Could not load item</p>
                        <p className="food-item-detail-empty-message">{error}</p>
                        <button type="button" className="add-button" onClick={loadItem}>
                            Try again
                        </button>
                    </div>
                ) : null}

                {!loading && !error && item ? (
                    <>
                        <section className="food-item-detail-hero">
                            <div className="food-item-detail-hero-media">
                                <img
                                    src={imageUrl || defaultFoodImage}
                                    alt={itemName}
                                    className="food-item-detail-hero-image"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = defaultFoodImage;
                                    }}
                                />
                            </div>
                            <div className="food-item-detail-hero-panel">
                                <h1 className="food-item-detail-title">
                                    <span className="food-item-detail-title-row">
                                        <DietTypeSymbol
                                            foodType={item.itemFoodType}
                                            className="food-item-detail-diet-symbol"
                                            size="lg"
                                        />
                                        <span className="food-item-detail-title-text">{itemName}</span>
                                    </span>
                                </h1>
                                <dl className="food-item-detail-facts">
                                    <DetailFact label="Category" value={categoryLine} />
                                    <DetailFact label="Cuisine origin" value={cuisineLine} />
                                    <DetailFact label="Price" value={priceLine} highlight alwaysShow />
                                    <DetailFact label="Quantity" value={servingLine} alwaysShow />
                                </dl>
                            </div>
                        </section>

                        <div className="food-item-detail-content">
                            {description || notes ? (
                                <section className="food-item-detail-section">
                                    <h2 className="food-item-detail-section-title">About this item</h2>
                                    {description ? (
                                        <p className="food-item-detail-prose">{description}</p>
                                    ) : null}
                                    {notes ? (
                                        <div className="food-item-detail-notes">
                                            <span className="food-item-detail-notes-label">Notes</span>
                                            <p>{notes}</p>
                                        </div>
                                    ) : null}
                                </section>
                            ) : null}

                            {ingredientNames.length > 0 ? (
                                <section className="food-item-detail-section">
                                    <h2 className="food-item-detail-section-title">Ingredients</h2>
                                    <div className="food-item-detail-chip-list">
                                        {ingredientNames.map((name) => (
                                            <span key={name} className="food-item-detail-chip">
                                                {name}
                                            </span>
                                        ))}
                                    </div>
                                </section>
                            ) : null}

                            {!isCatering && nutritionEntries.length > 0 ? (
                                <section className="food-item-detail-section">
                                    <h2 className="food-item-detail-section-title">
                                        Nutrition per serving
                                    </h2>
                                    <div className="food-item-detail-nutrition-grid">
                                        {nutritionEntries.map(([key, val]) => (
                                            <div key={key} className="food-item-detail-nutrition-card">
                                                <span className="food-item-detail-nutrition-value">
                                                    {val}
                                                </span>
                                                <span className="food-item-detail-nutrition-label">
                                                    {formatNutritionLabel(key)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ) : null}

                            {isJreats && agePricing.length > 0 ? (
                                <section className="food-item-detail-section">
                                    <h2 className="food-item-detail-section-title">
                                        Age-based portions
                                        <span className="food-item-detail-section-subtitle">
                                            Junior Eats pricing bands
                                        </span>
                                    </h2>
                                    <div className="food-item-detail-table-wrap">
                                        <table className="food-item-detail-table">
                                            <thead>
                                                <tr>
                                                    <th>Min age</th>
                                                    <th>Max age</th>
                                                    <th>Quantity</th>
                                                    <th>Price</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {agePricing.map((tier, index) => (
                                                    <tr key={tier.agePricingId ?? `tier-${index}`}>
                                                        <td>{displayValue(tier.minAge)}</td>
                                                        <td>{displayValue(tier.maxAge)}</td>
                                                        <td>{displayValue(tier.quantity)}</td>
                                                        <td className="food-item-detail-table-price">
                                                            {formatPriceWithCurrency(
                                                                tier.price,
                                                                item.Currency ?? item.currency
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={`food-item-detail-table-status ${
                                                                    String(tier.status).toUpperCase() ===
                                                                    'ACTIVE'
                                                                        ? 'food-item-detail-table-status--active'
                                                                        : 'food-item-detail-table-status--inactive'
                                                                }`}
                                                            >
                                                                {displayValue(tier.status)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            ) : null}
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );

    if (embedded) {
        return detailBody;
    }

    return <div className="home-container food-item-detail-page-outer">{detailBody}</div>;
};

function FoodItemDetailPage(props) {
    const { foodItemId: foodItemIdProp, embedded = false, ...rest } = props;
    const { foodItemId: routeFoodItemId } = useParams();

    const foodItemId = foodItemIdProp ?? routeFoodItemId;

    if (!embedded && routeFoodItemId && foodItemIdProp == null) {
        return <FoodItemDetailRouteRedirect foodItemId={routeFoodItemId} />;
    }

    return (
        <FoodItemDetailPageView
            foodItemId={foodItemId}
            embedded={embedded}
            {...rest}
        />
    );
}

export default FoodItemDetailPage;
